import { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  FormCheck,
} from 'react-bootstrap';
import { useAuth } from '@context';
import { useNavigate } from 'react-router-dom';
import { repositoryFactory } from '@data';

/**
 * Login Page Component
 * Proper login page with email and password validation
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const userRepository = repositoryFactory.getUserRepository();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [checkingBootstrap, setCheckingBootstrap] = useState(true);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [setupError, setSetupError] = useState(null);
  const [setupSuccess, setSetupSuccess] = useState(null);
  const [setupData, setSetupData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [setupValidationErrors, setSetupValidationErrors] = useState({});

  useEffect(() => {
    const checkBootstrap = async () => {
      try {
        const users = await userRepository.getAllUsers();
        setBootstrapRequired(users.length === 0);
      } catch {
        setBootstrapRequired(false);
      } finally {
        setCheckingBootstrap(false);
      }
    };

    checkBootstrap();
  }, [userRepository]);

  /**
    * Simple password hashing
   * In production, this should be done server-side
   */
  const hashPassword = (password) => {
    let hash = 0;
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  };

  /**
   * Validate form inputs
   */
  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!email.includes('@')) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSetupForm = () => {
    const errors = {};

    if (!setupData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!setupData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!setupData.email.includes('@')) {
      errors.email = 'Please enter a valid email address';
    }

    if (!setupData.password) {
      errors.password = 'Password is required';
    } else if (setupData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!setupData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (setupData.password !== setupData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setSetupValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateInitialAdmin = async (e) => {
    e.preventDefault();
    setSetupError(null);
    setSetupSuccess(null);

    if (!validateSetupForm()) {
      return;
    }

    setLoading(true);

    try {
      await userRepository.createInitialAdmin({
        name: setupData.name,
        email: setupData.email,
        password: setupData.password,
      });

      setBootstrapRequired(false);
      setSetupSuccess('Initial admin account created. Please sign in.');
      setEmail(setupData.email.trim().toLowerCase());
      setPassword('');
      setSetupData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
      setSetupValidationErrors({});
    } catch (err) {
      setSetupError(err.message || 'Failed to create initial admin account');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle login form submission
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const user = await userRepository.getUserByEmail(email);

      if (!user) {
        setError('No account found with this email address. Please check and try again.');
        setLoading(false);
        return;
      }

      // Check if user has stored password hash
      const storedPasswordHash = user.passwordHash;
      const enteredPasswordHash = hashPassword(password);

      if (!storedPasswordHash || storedPasswordHash !== enteredPasswordHash) {
        setError('Invalid email or password. Please try again.');
        setLoading(false);
        return;
      }

      // Login and redirect
      login(user);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingBootstrap) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-lg border-0">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" role="status" />
                <p className="mt-3 mb-0">Checking system setup...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Header className="bg-primary text-white py-4">
              <h2 className="mb-0 text-center">Adaptive Learning</h2>
              <p className="text-center text-light-secondary mt-2 mb-0">
                {bootstrapRequired ? 'Initial System Setup' : 'Sign In to Your Account'}
              </p>
            </Card.Header>
            <Card.Body className="p-5">
              {setupSuccess && (
                <Alert
                  variant="success"
                  dismissible
                  onClose={() => setSetupSuccess(null)}
                  className="mb-4"
                >
                  {setupSuccess}
                </Alert>
              )}

              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError(null)}
                  className="mb-4"
                >
                  {error}
                </Alert>
              )}

              {bootstrapRequired ? (
                <>
                  <Alert variant="info" className="mb-4">
                    No users found. Create the first admin account to complete one-time setup.
                  </Alert>

                  {setupError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setSetupError(null)}
                      className="mb-4"
                    >
                      {setupError}
                    </Alert>
                  )}

                  <Form onSubmit={handleCreateInitialAdmin}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your name"
                        value={setupData.name}
                        onChange={(e) => {
                          setSetupData({ ...setupData, name: e.target.value });
                          if (setupValidationErrors.name) {
                            setSetupValidationErrors({ ...setupValidationErrors, name: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.name}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.name && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Admin Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter admin email"
                        value={setupData.email}
                        onChange={(e) => {
                          setSetupData({ ...setupData, email: e.target.value });
                          if (setupValidationErrors.email) {
                            setSetupValidationErrors({ ...setupValidationErrors, email: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.email}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.email && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.email}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Create a strong password"
                        value={setupData.password}
                        onChange={(e) => {
                          setSetupData({ ...setupData, password: e.target.value });
                          if (setupValidationErrors.password) {
                            setSetupValidationErrors({ ...setupValidationErrors, password: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.password}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.password && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.password}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm password"
                        value={setupData.confirmPassword}
                        onChange={(e) => {
                          setSetupData({ ...setupData, confirmPassword: e.target.value });
                          if (setupValidationErrors.confirmPassword) {
                            setSetupValidationErrors({ ...setupValidationErrors, confirmPassword: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.confirmPassword}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.confirmPassword && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.confirmPassword}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      size="lg"
                      className="w-100 fw-semibold"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Creating Admin...
                        </>
                      ) : (
                        'Create Initial Admin'
                      )}
                    </Button>
                  </Form>
                </>
              ) : (

              <Form onSubmit={handleLogin}>
                {/* Email Field */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (validationErrors.email) {
                        setValidationErrors({
                          ...validationErrors,
                          email: '',
                        });
                      }
                    }}
                    isInvalid={!!validationErrors.email}
                    disabled={loading}
                    size="lg"
                    className="border-2"
                  />
                  {validationErrors.email && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Password Field */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (validationErrors.password) {
                        setValidationErrors({
                          ...validationErrors,
                          password: '',
                        });
                      }
                    }}
                    isInvalid={!!validationErrors.password}
                    disabled={loading}
                    size="lg"
                    className="border-2"
                  />
                  {validationErrors.password && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.password}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Remember Me Checkbox */}
                <FormCheck
                  className="mb-4"
                  type="checkbox"
                  id="rememberMe"
                  label="Remember my email"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />

                {/* Login Button */}
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  className="w-100 fw-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Signing In...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </Form>
              )}

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default LoginPage;
